import React, { useState, useEffect } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  IconButton,
  Paper,
  Tooltip,
  Typography
} from '@mui/material';
import AdminButton from 'components/common/AdminButton';
import {
  Cancel as CancelIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Warning as WarningIcon
} from '@mui/icons-material';

function EditableSection({ 
  title, 
  children, 
  onSave, 
  loading = false,
  error = null,
  disabled = false,
  helpText = null,
  onDirtyChange = null
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [localData, setLocalData] = useState(null);
  const [editKey, setEditKey] = useState(0); // Key to force re-render of children

  // Notify parent when dirty state changes
  React.useEffect(() => {
    if (onDirtyChange) {
      onDirtyChange(isDirty);
    }
  }, [isDirty, onDirtyChange]);

  const handleEdit = () => {
    setIsEditing(true);
    setIsDirty(false);
    setLocalData(null);
    setEditKey(prev => prev + 1); // Force children to reset
  };

  const handleCancel = () => {
    if (isDirty) {
      const confirmed = window.confirm('You have unsaved changes. Are you sure you want to cancel?');
      if (!confirmed) return;
    }
    setIsEditing(false);
    setIsDirty(false);
    setLocalData(null);
    setEditKey(prev => prev + 1); // Force children to reset
  };

  const handleSave = async () => {
    if (!onSave || !localData) return;
    
    setIsSaving(true);
    try {
      await onSave(localData);
      setIsEditing(false);
      setIsDirty(false);
      setLocalData(null);
    } catch (error) {
      console.error('Error saving section:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDataChange = (data) => {
    setLocalData(data);
    // Set dirty if there's actual data (not null)
    // The save button will separately check for change reason
    setIsDirty(data !== null);
  };

  // Clone children and inject editing props with key for reset
  const childrenWithProps = React.Children.map(children, child => {
    if (React.isValidElement(child)) {
      return React.cloneElement(child, { 
        key: editKey, // Force re-render on cancel/edit
        isEditing, 
        onDataChange: handleDataChange,
        disabled: disabled || isSaving
      });
    }
    return child;
  });

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
        <Box>
          <Typography variant="subtitle2" fontWeight="600">
            {title}
          </Typography>
          {helpText && !isEditing && (
            <Typography variant="caption" color="text.secondary">
              {helpText}
            </Typography>
          )}
        </Box>
        <Box>
          {!isEditing ? (
            <Tooltip title="Edit section">
              <IconButton 
                size="small" 
                onClick={handleEdit}
                disabled={disabled || loading}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : (
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <AdminButton
                size="small"
                variant="contained"
                startIcon={isSaving ? <CircularProgress size={16} /> : <SaveIcon />}
                onClick={handleSave}
                disabled={!isDirty || isSaving || !localData?.changeReason?.trim()}
                sx={{ minWidth: 'auto' }}
              >
                Save
              </AdminButton>
              <AdminButton
                size="small"
                variant="outlined"
                startIcon={<CancelIcon />}
                onClick={handleCancel}
                disabled={isSaving}
                sx={{ minWidth: 'auto' }}
              >
                Cancel
              </AdminButton>
            </Box>
          )}
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={24} />
        </Box>
      ) : (
        childrenWithProps
      )}
    </Paper>
  );
}

export default EditableSection;